/**
 * Playback progress store — remember where the listener paused so we can
 * offer a "Resume X:XX" button on the next visit.
 *
 * Storage shape (localStorage key `aird.progress.v1`):
 *   { [episodeId]: { seconds: number, duration?: number, updated: iso } }
 */
import { useCallback, useEffect, useState } from 'react';

const KEY = 'aird.progress.v1';
const EVENT = 'progress:change';
const MIN_SECONDS = 30;
const MIN_REMAINING = 30;

const readAll = () => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) || {} : {};
  } catch {
    return {};
  }
};

const writeAll = (map) => {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(map));
    window.dispatchEvent(new CustomEvent(EVENT, { detail: map }));
  } catch {
    /* quota */
  }
};

export const saveProgress = (id, seconds, duration) => {
  if (!id || typeof seconds !== 'number' || seconds < MIN_SECONDS) return;
  // If they're within the last 30s of the episode, treat as "finished" — remove.
  if (duration && duration - seconds < MIN_REMAINING) {
    return clearProgress(id);
  }
  const map = readAll();
  map[id] = {
    seconds: Math.floor(seconds),
    duration: duration ? Math.floor(duration) : undefined,
    updated: new Date().toISOString(),
  };
  writeAll(map);
};

export const getProgress = (id) => (id ? readAll()[id] || null : null);

export const clearProgress = (id) => {
  const map = readAll();
  if (!(id in map)) return;
  delete map[id];
  writeAll(map);
};

export const formatSeconds = (sec) => {
  if (!sec && sec !== 0) return '';
  const s = Math.floor(sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const two = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${two(m)}:${two(ss)}` : `${m}:${two(ss)}`;
};

export const useProgress = (id) => {
  const [state, setState] = useState(() => getProgress(id));
  useEffect(() => {
    if (!id) return undefined;
    const onLocal = () => setState(getProgress(id));
    const onStorage = (e) => { if (e.key === KEY) setState(getProgress(id)); };
    setState(getProgress(id));
    window.addEventListener(EVENT, onLocal);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT, onLocal);
      window.removeEventListener('storage', onStorage);
    };
  }, [id]);
  return state;
};
